import { publicApi } from "@api";
import { envConfig } from "@configs/env";
import { SEON_FEATURE } from "@configs/seon";
import { OTPMethod, OTPSource, RegistrationMethod, useStatsigTracking } from "@configs/statsig";
import { OnboardMode, Paths } from "@constants";
import { useLoginSubmit } from "@hooks/useLoginSubmit";
import { useMemberSettings } from "@hooks/useMemberSettings";
import usePreference from "@hooks/usePreference";
import { useSeonSession } from "@hooks/useSeonSession";
import { OnboardingDialogState } from "@types";
import { clearReferralCode, getReferralCode } from "@utils/referral";
import { APIErrorCodes, AuthToken, delay, standardizePhoneNumber, useHandleApiResponse } from "kz-ui-sdk";
import { PropsWithChildren, useCallback, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import FormOTP from "../FormOTP";
import FormVerifyPIN from "../FormVerifyPIN";

enum LoginSubPage {
  PIN,
  FORGOT_PIN,
  CREATE_NEW,
  OTP,
}

interface FormOTPAndPINProps extends PropsWithChildren {
  phone: string;
  onOTPComplete?: (values: { phone: string; otp: string; pin: string }) => void;
  mode: OnboardMode;
  deferUpdateToken?: boolean;
  onLoginSuccess?: (token?: AuthToken) => void;
  onRegisterSuccess?: () => void;
  onUpdatePINSuccess?: () => void;
  otpMode: "register" | "forgot-password";
}

// referral errors
const REFERRAL_ERROR_CODES: string[] = [
  APIErrorCodes.Member.AlreadyReferral,
  APIErrorCodes.Member.NotFoundReferralCode,
  APIErrorCodes.Member.ChainReferralLoop,
  APIErrorCodes.Member.MaxReferralReached,
  APIErrorCodes.Member.SelfReferralError,
];

export const DEFAULT_OTP_LENGTH = 6;

const FormOTPAndPIN = ({
  phone,
  mode,
  deferUpdateToken = false,
  onLoginSuccess,
  onUpdatePINSuccess,
  onRegisterSuccess,
  otpMode,
}: FormOTPAndPINProps) => {
  const [subPage, setSubPage] = useState<LoginSubPage>(LoginSubPage.OTP);
  const [OTPValue, setOTPValue] = useState("");
  const [errorMsgOTP, setErrorMsgOTP] = useState("");
  const { serverInfo } = usePreference();
  const { phonePrefix } = useMemberSettings();
  const [verifyOTP, { isLoading: isVerifyingOTP }] = publicApi.useVerifyOTPMutation();
  const formStartTime = useRef(Date.now());

  const { getSession: getSeonSession, isFetching: isFetchingSeonSession } = useSeonSession(SEON_FEATURE.REGISTER, {
    autoInit: true,
  });

  const [submitPIN, { isLoading: isSubmittingPIN }] =
    otpMode === "register" ? publicApi.useSubmitRegisterMutation() : publicApi.useSubmitResetPasswordMutation();

  const { handleApiResponse } = useHandleApiResponse({ toast });
  const {
    trackOTPVerified,
    trackOTPFailedAttempt,
    trackRegisterFormSubmit,
    trackRegistrationComplete,
    trackRegistrationFailed,
  } = useStatsigTracking();

  const { handleLogin, isLoading: isLoginLoading } = useLoginSubmit({
    onLoginSuccess,
    deferUpdateToken,
    suppressLoginTrack: mode === OnboardMode.REGISTER,
  });

  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleChangeSubPage = (step: LoginSubPage) => {
    setSubPage(step);
  };

  const handleOnOTPComplete = async (code: string) => {
    setOTPValue(code);
    if (code.length === (serverInfo?.onboard?.otpLength ?? DEFAULT_OTP_LENGTH)) await handleVerifyOTP(code);
  };

  const handleSubmitNewPIN = useCallback(
    async (pin: string) => {
      if (isSubmittingPIN) return;

      if (otpMode === "register") {
        trackRegisterFormSubmit(RegistrationMethod.PHONE_PIN, {
          timeSpentOnForm: Math.round((Date.now() - formStartTime.current) / 1000),
        });
      }

      // Send referral code and seon session in register mode
      let referralCode: string | undefined;
      if (mode === OnboardMode.REGISTER) {
        referralCode = getReferralCode();
      }
      const seonSession = await getSeonSession();

      const response = await submitPIN({
        phone: standardizePhoneNumber(phone, phonePrefix),
        accountId: envConfig.accountId,
        code: OTPValue,
        pin: pin,
        referralCode,
        seonSession,
      });

      handleApiResponse(response, {
        successMessage: t(mode === OnboardMode.REGISTER ? "Register successfully!" : "pin updated successfully"),
        toastError: false,
        onSuccess: () => {
          // Track registration complete for Phone+PIN flow
          if (mode === OnboardMode.REGISTER) {
            trackRegistrationComplete(RegistrationMethod.PHONE_PIN, {
              member: response?.data?.member,
            });
          }

          delay().then(() => {
            handleLogin(phone, pin);
            // Clear referral code upon use
            if (referralCode) {
              clearReferralCode();
            }
            if (mode === OnboardMode.REGISTER) {
              onRegisterSuccess?.();
            } else {
              onUpdatePINSuccess?.();
            }
          });
        },
        onError: (error) => {
          if (mode === OnboardMode.REGISTER) {
            trackRegistrationFailed(RegistrationMethod.PHONE_PIN, error);
          }
          // If referral error, show toast error, clear refCode and redirect back to register page
          if (error?.data?.error?.message && REFERRAL_ERROR_CODES.includes(error?.data?.error?.message)) {
            toast.error(t(error?.data?.error?.message));
            clearReferralCode();
            navigate(generatePath(Paths.PUBLIC.ONBOARD_REGISTER_DIALOG), {
              replace: true,
              state: { openOnboarding: true, mode: OnboardMode.REGISTER } as OnboardingDialogState,
            });
            return;
          }

          setOTPValue("");
          //If PIN is ok but OTP is incorrect, redirect back to OTP form, but keep the PIN
          //User have to re-enter OTP, if correct then log them in immediately
          setSubPage(LoginSubPage.OTP);
          setErrorMsgOTP(error?.data?.error?.message ?? "");
        },
      });
    },
    [
      OTPValue,
      handleApiResponse,
      isSubmittingPIN,
      mode,
      navigate,
      onUpdatePINSuccess,
      phone,
      submitPIN,
      t,
      phonePrefix,
      onRegisterSuccess,
      handleLogin,
      trackRegistrationComplete,
      trackRegisterFormSubmit,
      otpMode,
      getSeonSession,
      trackRegistrationFailed,
    ],
  );

  const handleVerifyOTP = useCallback(
    async (code: string) => {
      const resVerifyOTP = await verifyOTP({
        phone: standardizePhoneNumber(phone, phonePrefix),
        code: code,
        accountId: envConfig.accountId,
      });

      handleApiResponse(resVerifyOTP, {
        toastError: false,
        toastSuccess: false,
        onSuccess: () => {
          // Track OTP verification success
          trackOTPVerified(OTPMethod.SMS, {
            source: otpMode === "register" ? OTPSource.REGISTER : OTPSource.FORGOT_PASSWORD,
          });
          handleChangeSubPage(LoginSubPage.PIN);
        },
        onError: (error) => {
          // Track OTP verification failure
          trackOTPFailedAttempt(error, {
            source: otpMode === "register" ? OTPSource.REGISTER : OTPSource.FORGOT_PASSWORD,
          });
          if (error?.data?.error?.message) {
            setErrorMsgOTP(t(error?.data?.error.message));
          }
        },
      });
    },
    [handleApiResponse, phone, t, verifyOTP, phonePrefix, trackOTPVerified, trackOTPFailedAttempt, otpMode],
  );

  const isFormLoading = useMemo(() => {
    return isLoginLoading || isSubmittingPIN || isVerifyingOTP || isFetchingSeonSession;
  }, [isLoginLoading, isSubmittingPIN, isVerifyingOTP, isFetchingSeonSession]);

  return (
    <div className="mx-auto w-full max-w-full">
      {subPage === LoginSubPage.OTP && (
        <FormOTP
          OTPValue={OTPValue}
          onComplete={handleOnOTPComplete}
          phone={phone}
          errorMessage={errorMsgOTP}
          mode={otpMode}
          loading={isVerifyingOTP}
        />
      )}
      {subPage === LoginSubPage.PIN && (
        <FormVerifyPIN
          phone={phone}
          loading={isFormLoading}
          mode={mode}
          onSubmit={(values) => {
            handleSubmitNewPIN(values?.pin as string);
          }}
        />
      )}
    </div>
  );
};

export default FormOTPAndPIN;
